import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { debounce } from "lodash-es";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { BwSearchLine } from "@/components/ui/icons";
import { Input } from "@/components/ui/input/input";
import { useQueryState } from "@/hooks/use-query-state";
import { useFilterUrlKeys } from "../filter-url-keys";

/**
 * Debounced (300ms) list search, synced to the `search` query param (or the
 * nearest `FilterUrlKeysProvider`'s search key). Shared across every migrated
 * list (ported from `components/filters/header-search`).
 */
export const FilterSearch = () => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, toggle] = useToggle(false);
  const [searchParam, setSearchParam] = useQueryState(
    useFilterUrlKeys().search,
  );
  const [keyword, setKeyword] = useState(searchParam || "");

  useEffect(() => {
    setKeyword(searchParam || "");
  }, [searchParam]);

  const debouncedUpdateParam = useMemo(
    () =>
      debounce((value: string) => {
        setSearchParam(value || null);
      }, 300),
    [setSearchParam],
  );

  useEffect(() => {
    return () => debouncedUpdateParam.cancel();
  }, [debouncedUpdateParam]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setKeyword(value);
    debouncedUpdateParam(value);
  };

  const handleBlur = () => {
    // An open-but-empty field is pure noise once the user looks elsewhere, so
    // collapse it. A field with a keyword stays open — it is showing why the
    // list is filtered.
    if (keyword) return;
    toggle(false);
  };

  const handleSearchOpen = () => {
    toggle();

    // Closing: drop focus too, or the field stays focused while clipped to 0px
    // and the next keystroke lands in an invisible input.
    if (open) {
      inputRef.current?.blur();
      return;
    }

    setTimeout(() => {
      inputRef.current?.focus();
    }, 250);
  };

  const expanded = open || keyword;

  return (
    <div
      className={cn(
        /* sorted */
        "group relative flex",
        "items-center",
        "h-8 min-w-8",
        "rounded-full",
        "hover:bg-bg-dark-min",
        { "bg-bg-dark-min": expanded },
      )}
      data-test="search-list"
    >
      <div
        className={cn(
          /* sorted */
          "absolute right-0 flex",
          "items-center",
          "bg-bg-light",
          "md:relative md:bg-transparent",
        )}
      >
        <div
          className="p-1.5 cursor-pointer"
          onClick={handleSearchOpen}
          // Keep focus in the input through the press: without this, the blur
          // fires first, collapses an empty field, and the click that follows
          // re-opens it instead of closing it.
          onMouseDown={(event) => event.preventDefault()}
        >
          <BwSearchLine className="text-text-dark-max" size={20} />
        </div>

        <AnimatePresence initial={false}>
          <motion.div
            key="search-box"
            className="overflow-hidden"
            initial={{ width: 0 }}
            animate={{ width: expanded ? 200 : 0 }}
            exit={{ width: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <Input
              ref={inputRef}
              placeholder="Type to search..."
              className={cn(
                /* sorted */
                "h-8 w-50",
                "pl-0",
                "text-sm font-normal leading-5",
                "rounded-none bg-transparent outline-none border-none",
              )}
              name="search"
              value={keyword}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
